/**
 * Chicago Author-Date (18th edition) bibliography formatter.
 *
 * Rules implemented:
 * - Author format in bibliography: LastName, FirstName / for subsequent authors: FirstName LastName
 * - 1–6 authors: list all; 7+ → first author + et al.
 * - Year placed immediately after author list
 * - Article: Last, First, and First2 Last2. Year. "Title." Journal Vol (Issue): Pages. DOI/URL.
 * - Book: Last, First. Year. Title. Publisher.
 * - Chapter: Last, First. Year. "Chapter Title." In Book Title, edited by First Last, Pages. Publisher.
 * - Sorted alphabetically by first author's last name
 *
 * Reference: Chicago Manual of Style, 17th/18th ed., Author-Date system.
 */

import type {
    CslJson,
    ReferenceItem,
} from '@/components/references/ReferenceListItem.vue';

// ── Author helpers ────────────────────────────────────────────────────────────

interface CslName {
    family?: string;
    given?: string;
    literal?: string;
}

function formatFirstAuthor(name: CslName): string {
    if (name.literal) {
        return name.literal;
    }

    const family = name.family ?? '';
    const given = name.given ?? '';

    return given ? `${family}, ${given}` : family;
}

function formatSubsequentAuthor(name: CslName): string {
    if (name.literal) {
        return name.literal;
    }

    const family = name.family ?? '';
    const given = name.given ?? '';

    return given ? `${given} ${family}` : family;
}

function formatChicagoAuthorList(authors: CslName[]): string {
    if (!authors.length) {
        return '';
    }

    if (authors.length === 1) {
        return formatFirstAuthor(authors[0]);
    }

    if (authors.length > 6) {
        return formatFirstAuthor(authors[0]) + ' et al.';
    }

    const first = formatFirstAuthor(authors[0]);
    const rest = authors.slice(1).map(formatSubsequentAuthor);

    if (authors.length === 2) {
        return `${first}, and ${rest[0]}`;
    }

    return (
        first +
        ', ' +
        rest.slice(0, -1).join(', ') +
        ', and ' +
        rest[rest.length - 1]
    );
}

// ── Plain-text fallback ───────────────────────────────────────────────────────

function splitLastFirst(fullName: string): { last: string; first: string } {
    const parts = fullName.trim().split(/\s+/);

    if (parts.length < 2) {
        return { last: fullName, first: '' };
    }

    return {
        last: parts[parts.length - 1],
        first: parts.slice(0, -1).join(' '),
    };
}

function formatFallbackAuthorList(authors: string[]): string {
    if (!authors.length) {
        return '';
    }

    const mapped: CslName[] = authors.map((a) => {
        const { last, first } = splitLastFirst(a);

        return { family: last, given: first };
    });

    return formatChicagoAuthorList(mapped);
}

// ── Year extraction ───────────────────────────────────────────────────────────

function cslYear(csl: CslJson): string {
    const y = csl.issued?.['date-parts']?.[0]?.[0];

    return y && Number(y) > 0 ? String(y) : '';
}

/** Assemble "Author. year. rest" — uses n.d. when year is absent. */
function buildEntry(authorStr: string, year: string, rest: string): string {
    const y = year || 'n.d.';

    return authorStr ? `${authorStr}. ${y}. ${rest}` : `${y}. ${rest}`;
}

// ── Per-type formatters ───────────────────────────────────────────────────────

function formatArticleJournal(csl: CslJson): string {
    const authorStr = formatChicagoAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const journal = csl['container-title'] ?? '';
    const vol = csl.volume ?? '';
    const issue = csl.issue ? ` (${csl.issue})` : '';
    const pages = csl.page ? `: ${csl.page}` : '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';
    const url = !csl.DOI && csl.URL ? ` ${csl.URL}` : '';

    const sourceStr = journal + (vol ? ' ' + vol + issue + pages : '') + '.';

    return buildEntry(authorStr, year, `"${title}." ${sourceStr}${doi}${url}`);
}

function formatBook(csl: CslJson): string {
    const authorStr = formatChicagoAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const edition = csl.edition ? ` ${csl.edition} ed.` : '';
    const publisher = csl.publisher ?? '';
    const place = csl['publisher-place'] ?? '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';
    const url = !csl.DOI && csl.URL ? ` ${csl.URL}` : '';

    const pubStr = [place, publisher].filter(Boolean).join(': ');

    return buildEntry(
        authorStr,
        year,
        `${title}${edition}. ${pubStr}.${doi}${url}`,
    );
}

function formatChapter(csl: CslJson): string {
    const authorStr = formatChicagoAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const bookTitle = csl['container-title'] ?? '';
    const editors = (csl.editor ?? []).map(formatSubsequentAuthor).join(', ');
    const editorStr = editors ? `, edited by ${editors}` : '';
    const pages = csl.page ? `, ${csl.page}` : '';
    const publisher = csl.publisher ?? '';
    const place = csl['publisher-place'] ?? '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';

    const pubStr = [place, publisher].filter(Boolean).join(': ');

    return buildEntry(
        authorStr,
        year,
        `"${title}." In ${bookTitle}${editorStr}${pages}. ${pubStr}.${doi}`,
    );
}

function formatThesis(csl: CslJson): string {
    const authorStr = formatChicagoAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const genre = csl.genre ? `${csl.genre}, ` : '';
    const publisher = csl.publisher ?? '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';
    const url = !csl.DOI && csl.URL ? ` ${csl.URL}` : '';

    return buildEntry(
        authorStr,
        year,
        `"${title}." ${genre}${publisher}.${doi}${url}`,
    );
}

function formatWebpage(csl: CslJson): string {
    const authorStr = formatChicagoAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const siteName = csl['container-title'] ?? '';
    const url = csl.URL ? ` ${csl.URL}` : '';

    const siteStr = siteName ? `${siteName}.` : '';

    return buildEntry(authorStr, year, `"${title}." ${siteStr}${url}`);
}

// ── Fallback using plain string authors ───────────────────────────────────────

function formatFallback(ref: ReferenceItem): string {
    const authors = Array.isArray(ref.authors)
        ? ref.authors
        : [ref.authors ?? ''];
    const authorStr = formatFallbackAuthorList(authors);
    const year = ref.year && ref.year > 0 ? String(ref.year) : '';
    const doi = ref.doi ? ` https://doi.org/${ref.doi}` : '';

    return buildEntry(authorStr, year, `"${ref.title}."${doi}`);
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Format a single reference in Chicago Author-Date bibliography style.
 */
export function formatChicagoBibliography(ref: ReferenceItem): string {
    const csl = ref.csl;

    if (!csl) {
        return formatFallback(ref);
    }

    switch (csl.type) {
        case 'article-journal':
        case 'article-magazine':
        case 'article-newspaper':
            return formatArticleJournal(csl);
        case 'book':
            return formatBook(csl);
        case 'chapter':
            return formatChapter(csl);
        case 'thesis':
            return formatThesis(csl);
        case 'webpage':
        case 'post-weblog':
        case 'online':
            return formatWebpage(csl);
        default:
            return csl['container-title']
                ? formatArticleJournal(csl)
                : formatBook(csl);
    }
}

/**
 * Sort references alphabetically by first author last name (Chicago rule).
 */
export function sortChicago(refs: ReferenceItem[]): ReferenceItem[] {
    return [...refs].sort((a, b) => {
        const aA = Array.isArray(a.authors)
            ? (a.authors[0] ?? '')
            : (a.authors ?? '');
        const bA = Array.isArray(b.authors)
            ? (b.authors[0] ?? '')
            : (b.authors ?? '');
        const aLast = aA.trim().split(/\s+/).at(-1) ?? aA;
        const bLast = bA.trim().split(/\s+/).at(-1) ?? bA;

        return aLast.localeCompare(bLast, undefined, { sensitivity: 'base' });
    });
}

/**
 * Format the inline Chicago Author-Date citation label.
 * Returns "(Author Year)" or "(Author et al. Year)".
 */
export function formatChicagoInline(ref: ReferenceItem): string {
    const authors = Array.isArray(ref.authors)
        ? ref.authors
        : [ref.authors ?? ''];
    const firstAuthor = authors[0] ?? '';
    const parts = firstAuthor.trim().split(/\s+/);
    const lastName = parts.at(-1) ?? firstAuthor;
    const etAl = authors.length > 2 ? ' et al.' : '';
    const year = ref.year ?? '';

    return `(${lastName}${etAl} ${year})`;
}
